import React, { useState } from "react";

export const AllCoachesModal = ({ isOpen, onClose, coaches = [], teamName = "" }) => {
  const [expandedBios, setExpandedBios] = useState({});

  const toggleBio = (index) => {
    setExpandedBios(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-jp-card rounded-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden border border-jp-line">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-jp-line">
          <h2 className="text-xl font-semibold text-jp-ink">
            {teamName ? `${teamName} - Coaching Staff` : "Coaching Staff"}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-jp-muted hover:text-jp-ink transition-colors rounded-lg hover:bg-jp-section"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Coaches List */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-80px)]">
          {coaches.length === 0 ? (
            <div className="text-center text-jp-muted py-8">
              No coaching staff information available
            </div>
          ) : (
            <div className="space-y-4">
              {coaches.map((coach, index) => (
                <div
                  key={index}
                  className="bg-jp-section rounded-lg p-4 border border-jp-line"
                >
                  <div className="flex flex-col sm:flex-row gap-4">
                    {/* Coach Photo */}
                    {coach.photoUrl && (
                      <div className="shrink-0">
                        <img
                          src={coach.photoUrl}
                          alt={coach.name}
                          className="w-24 h-24 sm:w-32 sm:h-32 object-cover rounded-lg"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}

                    {/* Coach Info */}
                    <div className="flex-1 min-w-0">
                      {/* Name & Title */}
                      <div className="mb-3 text-left">
                        <h3 className="text-lg font-semibold text-jp-ink text-left">{coach.name}</h3>
                        {coach.title && (
                          <span className="text-sm text-jp-accent ">{coach.title}</span>
                        )}
                      </div>

                      {/* Contact Info */}
                      <div className="flex flex-wrap gap-3 mb-3 text-sm">
                        {coach.email && (
                          <a
                            href={`mailto:${coach.email}`}
                            className="flex items-center gap-2 text-jp-muted hover:text-jp-accent transition-colors"
                          >
                            <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                              <polyline points="22,6 12,13 2,6"/>
                            </svg>
                            <span className="truncate">{coach.email}</span>
                          </a>
                        )}
                        {coach.phone && (
                          <a
                            href={`tel:${coach.phone}`}
                            className="flex items-center gap-2 text-jp-muted hover:text-jp-accent transition-colors"
                          >
                            <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                            </svg>
                            <span>{coach.phone}</span>
                          </a>
                        )}
                      </div>

                      {/* Social Links */}
                      <div className="flex gap-3 mb-3">
                        {coach.twitter && (
                          <a
                            href={coach.twitter}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-jp-section rounded-lg text-jp-muted hover:text-jp-accent hover:bg-jp-section transition-colors"
                            title="Twitter/X"
                          >
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                            </svg>
                          </a>
                        )}
                        {coach.instagram && (
                          <a
                            href={coach.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-jp-section rounded-lg text-jp-muted hover:text-pink-400 hover:bg-jp-section transition-colors"
                            title="Instagram"
                          >
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                              <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                            </svg>
                          </a>
                        )}
                        {coach.sourceUrl && (
                          <a
                            href={coach.sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-jp-section rounded-lg text-jp-muted hover:text-jp-accent hover:bg-jp-section transition-colors"
                            title="View Profile"
                          >
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                              <polyline points="15 3 21 3 21 9"/>
                              <line x1="10" y1="14" x2="21" y2="3"/>
                            </svg>
                          </a>
                        )}
                      </div>

                      {/* Bio */}
                      {coach.bio && (
                        <div className="text-left">
                          <p className={`text-sm text-jp-muted text-left ${expandedBios[index] ? '' : 'line-clamp-3'}`}>
                            {coach.bio}
                          </p>
                          {coach.bio.length > 200 && (
                            <button
                              onClick={() => toggleBio(index)}
                              className="text-jp-accent text-sm mt-1 hover:text-jp-accent/80 transition-colors"
                            >
                              {expandedBios[index] ? 'See Less' : 'See More'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
